import { CSSProperties, FC, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  IRouterBridgeTab,
  ISlideshowSlide,
  ISlideshowTab,
  slideshowTabs,
} from "../../../data/crystalPageContent";
import styles from "./index.module.css";

const isRouterBridgeTab = (
  tab: ISlideshowTab | IRouterBridgeTab
): tab is IRouterBridgeTab => tab.kind === "special-dual";

const CARD_STEP = 1400;
// Fraction of a card you must drag before we commit to the next/prev slide.
const PAGE_RATIO = 0.1;

function scrollerMaxIndex(scroller: HTMLDivElement) {
  return Math.max(
    0,
    Math.round((scroller.scrollHeight - scroller.clientHeight) / CARD_STEP)
  );
}

function pagedIndex(startScroll: number, currentScroll: number, maxI: number) {
  const from = Math.round(startScroll / CARD_STEP);
  const delta = currentScroll - startScroll;
  if (delta > CARD_STEP * PAGE_RATIO) return Math.min(maxI, from + 1);
  if (delta < -CARD_STEP * PAGE_RATIO) return Math.max(0, from - 1);
  return from;
}

const SNAP_ANIM_MS = 320;

function animateScrollTop(el: HTMLElement, to: number, ms = SNAP_ANIM_MS) {
  const from = el.scrollTop;
  if (Math.abs(from - to) < 2) {
    el.scrollTop = to;
    return;
  }
  const start = performance.now();
  const tick = (now: number) => {
    const t = Math.min(1, (now - start) / ms);
    const eased = 1 - (1 - t) * (1 - t);
    el.scrollTop = from + (to - from) * eased;
    if (t < 1) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

// Chrome/Edge fire sparser, smaller deltaX than Safari for the same
// trackpad swipe, and their 120ms gaps can look like "gesture ended".
const isBlink =
  typeof window !== "undefined" && "chrome" in window;

function renderSlideMedia(
  src: string,
  alt: string,
  className: string,
  style?: CSSProperties
) {
  return (
    <img
      key={src}
      src={src}
      alt={alt}
      className={className}
      style={style}
      loading="lazy"
    />
  );
}

// Renders body/bullet copy with a bolded lead-in, matching how the design
// calls out the leading term. Two forms are supported:
// - explicit "**bold**" markdown-style spans, anywhere in the text
// - a bare "Label: rest of sentence" prefix (no markdown needed), e.g.
//   "Control: Apply sophisticated rules to control traffic flows."
// Text with neither form renders as plain text.
function renderRichText(text: string) {
  if (text.includes("**")) {
    const parts = text.split(/\*\*(.+?)\*\*/g);
    return parts.map((part, i) =>
      i % 2 === 1 ? <strong key={i}>{part}</strong> : part
    );
  }
  const match = text.match(/^([A-Za-z][^:]{0,40}):\s(.*)$/s);
  if (!match) return text;
  const [, label, rest] = match;
  return (
    <>
      <strong>{label}:</strong> {rest}
    </>
  );
}

// The inner content of one slide (title/text/images) - shared by the desktop
// scroll-per-slide rendering and the mobile single-slide rendering below.
function renderSlideBody(
  slide: ISlideshowSlide,
  activeTab: ISlideshowTab
) {
  const stackedImage =
    slide.layout === "text-stack-plus-image" ? slide.images[0] : undefined;
  const columnImages = stackedImage ? slide.images.slice(1) : slide.images;
  return (
    <>
      <div
        className={styles["page-text"]}
        style={slide.textWidth ? { flexBasis: slide.textWidth } : undefined}
      >
        <div className={styles["page-title"]}>
          <img src={activeTab.titleIcon} alt="" className={styles["page-title-icon"]} />
          <span className={styles["page-title-label"]}>{activeTab.label}</span>
        </div>
        {slide.heading && <h3>{slide.heading}</h3>}
        {slide.subheading && <p className={styles["subheading"]}>{slide.subheading}</p>}
        {slide.body && <p>{renderRichText(slide.body)}</p>}
        {slide.bullets && (
          <ul>
            {slide.bullets.map((b) => (
              <li key={b}>{renderRichText(b)}</li>
            ))}
          </ul>
        )}
        {stackedImage && renderSlideMedia(stackedImage, slide.alt, styles["stacked-image"])}
      </div>
      <div className={styles["page-images"]}>
        {columnImages.length === 0 ? (
          <div className={styles["image-placeholder"]}>{slide.alt} — pending image asset</div>
        ) : (
          columnImages.map((src) =>
            renderSlideMedia(
              src,
              slide.alt,
              styles["page-image"],
              slide.imageHeight ? { height: slide.imageHeight } : undefined
            )
          )
        )}
      </div>
    </>
  );
}

// The 5-category switchable slideshow described in
// https://github.com/firewalla/firecommit/issues/9484#issuecomment-5600394270:
// each slide is a fixed-size 1400x750 card. The user pages through them by
// scrolling the mouse wheel/trackpad - the scroller itself is a real
// overflow-y container rotated 90deg (see the CSS), so vertical wheel gets
// genuine native scroll physics (momentum, snap). Visual left/right is
// mapped onto that same scroller so the two axes feel the same. At the
// first/last slide, paging continues into the adjacent tab.
// Router Mode & Bridge Mode gets the spec's Special Layout instead.
const MOBILE_BREAKPOINT = 900;

const FeatureSlideshow: FC = () => {
  const [activeKey, setActiveKey] = useState(slideshowTabs[0].key);
  const [slideIndex, setSlideIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Smooth scroll fires onScroll for every card it passes. Ignore those
  // in-transit values and only commit the active dot once the animation
  // settles (scrollend, or the timeout below if the browser has no
  // scrollend).
  const isProgrammaticScroll = useRef(false);
  const settleTimer = useRef<number>();
  const pendingSlideIndex = useRef<number | null>(null);
  const tabLandingIndex = useRef(0);

  const activeTab = useMemo(
    () => slideshowTabs.find((t) => t.key === activeKey)!,
    [activeKey]
  );

  const panelRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const scaleRef = useRef(scale);
  scaleRef.current = scale;
  const [isMobileLayout, setIsMobileLayout] = useState(
    typeof window !== "undefined" && window.innerWidth <= MOBILE_BREAKPOINT
  );

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    function update() {
      if (!panel) return;
      const mobile = window.innerWidth <= MOBILE_BREAKPOINT;
      setIsMobileLayout(mobile);
      setScale(mobile ? 1 : Math.min(1, panel.clientWidth / CARD_STEP));
    }
    update();
    const ro = new ResizeObserver(update);
    ro.observe(panel);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  const scaleWrapperStyle = isMobileLayout
    ? undefined
    : { width: CARD_STEP * scale, height: 750 * scale };
  const scaledContentStyle = isMobileLayout
    ? undefined
    : { transform: `scale(${scale})`, transformOrigin: "top left" };

  function selectTab(key: string, landing: "start" | "end" = "start") {
    const tab = slideshowTabs.find((t) => t.key === key);
    const last =
      tab && !isRouterBridgeTab(tab) ? Math.max(0, tab.slides.length - 1) : 0;
    const i = landing === "end" ? last : 0;
    tabLandingIndex.current = i;
    pendingSlideIndex.current = i;
    setActiveKey(key);
    setSlideIndex(i);
  }

  // Panel remounts with key={activeKey}, so scrollTop must be applied on
  // the new scroller. Going backward lands on that tab's last slide.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = tabLandingIndex.current * CARD_STEP;
  }, [activeKey]);

  function commitSlideIndex(force = false) {
    const el = scrollRef.current;
    if (!el) {
      isProgrammaticScroll.current = false;
      pendingSlideIndex.current = null;
      return;
    }
    const snapped = Math.round(el.scrollTop / CARD_STEP);
    // A spurious `scrollend` can fire before the smooth scroll actually
    // settles; ignore those so the active dot doesn't jump mid-animation.
    if (!force && Math.abs(el.scrollTop - snapped * CARD_STEP) > 2) return;
    isProgrammaticScroll.current = false;
    pendingSlideIndex.current = null;
    setSlideIndex(snapped);
  }

  function goToSlide(i: number) {
    pendingSlideIndex.current = i;
    if (isMobileLayout || !scrollRef.current) {
      setSlideIndex(i);
      return;
    }
    isProgrammaticScroll.current = true;
    scrollRef.current.scrollTo({ top: i * CARD_STEP, behavior: "smooth" });
    window.clearTimeout(settleTimer.current);
    // Safety net for browsers without `scrollend`.
    settleTimer.current = window.setTimeout(() => commitSlideIndex(true), 800);
  }

  // Shared by vertical wheel (at a tab's first/last slide), horizontal
  // trackpad swipe, and left/right touch: move one slide, or one tab when
  // there isn't another slide in that direction.
  function page(direction: 1 | -1): boolean {
    const tabIdx = slideshowTabs.findIndex((t) => t.key === activeKey);
    const tab = slideshowTabs[tabIdx];
    if (tab && !isRouterBridgeTab(tab)) {
      const el = scrollRef.current;
      const i =
        pendingSlideIndex.current ??
        (el && !isMobileLayout
          ? Math.round(el.scrollTop / CARD_STEP)
          : slideIndex);
      const next = i + direction;
      if (next >= 0 && next < tab.slides.length) {
        goToSlide(next);
        return true;
      }
    }
    const nextTab = slideshowTabs[tabIdx + direction];
    if (!nextTab) return false;
    selectTab(nextTab.key, direction < 0 ? "end" : "start");
    return true;
  }
  const pageRef = useRef(page);
  pageRef.current = page;

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    function onScrollEnd() {
      commitSlideIndex();
    }
    el.addEventListener("scrollend", onScrollEnd);
    return () => el.removeEventListener("scrollend", onScrollEnd);
  }, [activeKey]);

  // Native overflow-y (rotated to look horizontal) already pages slides
  // for vertical wheel; only intercept at the ends so scrolling past the
  // last (or back past the first) continues into the next/previous tab.
  // The last tab (router/bridge) has no scroller at all, so the same
  // vertical wheel on the panel must still page to the previous tab.
  useEffect(() => {
    if (isMobileLayout) return;
    const panel = panelRef.current;
    if (!panel) return;
    const TAB_THRESHOLD = 40;
    const RESTING_DELTA = 4;
    let accY = 0;
    let tabLocked = false;

    function onWheel(e: WheelEvent) {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
      const scroller = scrollRef.current;
      if (scroller) {
        if (e.deltaY > 0) {
          if (scroller.scrollTop + scroller.clientHeight < scroller.scrollHeight - 1) return;
          if (!pageRef.current(1)) return;
          e.preventDefault();
        } else if (e.deltaY < 0) {
          if (scroller.scrollTop > 1) return;
          if (!pageRef.current(-1)) return;
          e.preventDefault();
        }
        return;
      }
      // Last tab has no next page: let downward wheel chain to the document.
      if (e.deltaY >= 0) return;
      e.preventDefault();
      if (tabLocked) {
        if (Math.abs(e.deltaY) <= RESTING_DELTA) {
          tabLocked = false;
          accY = 0;
        }
        return;
      }
      accY += e.deltaY;
      if (Math.abs(accY) < TAB_THRESHOLD) return;
      const direction: 1 | -1 = accY > 0 ? 1 : -1;
      accY = 0;
      if (!pageRef.current(direction)) return;
      tabLocked = true;
    }
    panel.addEventListener("wheel", onWheel, { passive: false });
    return () => panel.removeEventListener("wheel", onWheel);
  }, [isMobileLayout]);

  // Pointer drag (mouse, touch, pen) maps onto the same overflow-y
  // scroller. Discrete page() is only used when there is no scroller
  // (router/bridge) or on mobile.
  //
  // pointermove must be non-passive so preventDefault can stop the
  // browser stealing a touch/pen gesture (scroll/back → pointercancel).
  useEffect(() => {
    const maybePanel = panelRef.current;
    if (!maybePanel) return;
    const host: HTMLDivElement = maybePanel;
    let start: { x: number; y: number } | null = null;
    let startScroll = 0;
    let axis: "x" | "y" | null = null;
    let pointerId: number | null = null;
    const AXIS_LOCK_THRESHOLD = 10;
    const SWIPE_THRESHOLD = 50;

    function isInteractive(target: EventTarget | null) {
      return (
        target instanceof Element &&
        !!target.closest("button, a, input, select, textarea")
      );
    }

    function onPointerDown(e: PointerEvent) {
      if (e.pointerType === "mouse" && e.button !== 0) return;
      if (isInteractive(e.target)) return;
      start = { x: e.clientX, y: e.clientY };
      startScroll = scrollRef.current?.scrollTop ?? 0;
      axis = null;
      pointerId = e.pointerId;
      host.setPointerCapture(e.pointerId);
    }

    function onPointerMove(e: PointerEvent) {
      if (!start || e.pointerId !== pointerId) return;
      const dx = e.clientX - start.x;
      const dy = e.clientY - start.y;
      if (!axis && (Math.abs(dx) > AXIS_LOCK_THRESHOLD || Math.abs(dy) > AXIS_LOCK_THRESHOLD)) {
        axis = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
      }
      if (axis === "x") {
        e.preventDefault();
        host.style.cursor = "grabbing";
        host.style.userSelect = "none";
        const scroller = scrollRef.current;
        if (!scroller) return;
        scroller.style.scrollSnapType = "none";
        const visualScale = Math.max(scaleRef.current, 0.01);
        scroller.scrollTop = startScroll - dx / visualScale;
        return;
      }
      if (axis === "y" && !scrollRef.current) {
        e.preventDefault();
      }
    }

    function finish(e: PointerEvent) {
      if (pointerId !== null && e.pointerId !== pointerId) return;
      const scroller = scrollRef.current;
      host.style.cursor = "";
      host.style.userSelect = "";
      if (pointerId !== null) {
        try {
          host.releasePointerCapture(pointerId);
        } catch {
          /* already released */
        }
      }
      pointerId = null;
      if (!start || (axis !== "x" && !(axis === "y" && !scroller))) {
        if (scroller) scroller.style.scrollSnapType = "";
        start = null;
        axis = null;
        return;
      }
      const dx = e.clientX - start.x;
      const dy = e.clientY - start.y;
      const dragAxis = axis;
      start = null;
      axis = null;
      if (scroller && dragAxis === "x") {
        const maxI = scrollerMaxIndex(scroller);
        const i = pagedIndex(startScroll, scroller.scrollTop, maxI);
        if (dx < -SWIPE_THRESHOLD && i >= maxI && startScroll >= maxI * CARD_STEP - 1) {
          scroller.style.scrollSnapType = "";
          pageRef.current(1);
          return;
        }
        if (dx > SWIPE_THRESHOLD && i <= 0 && startScroll <= 1) {
          scroller.style.scrollSnapType = "";
          pageRef.current(-1);
          return;
        }
        // Keep snap off until the tween finishes; restoring mandatory snap
        // first makes Chrome jump with no page-turn animation.
        animateScrollTop(scroller, i * CARD_STEP);
        window.setTimeout(() => {
          scroller.style.scrollSnapType = "";
        }, SNAP_ANIM_MS + 40);
        return;
      }
      if (scroller) scroller.style.scrollSnapType = "";
      if (dragAxis === "y") {
        if (Math.abs(dy) < SWIPE_THRESHOLD) return;
        pageRef.current(dy < 0 ? -1 : 1);
        return;
      }
      if (Math.abs(dx) < SWIPE_THRESHOLD) return;
      pageRef.current(dx < 0 ? 1 : -1);
    }

    host.addEventListener("pointerdown", onPointerDown);
    host.addEventListener("pointermove", onPointerMove, { passive: false });
    host.addEventListener("pointerup", finish);
    host.addEventListener("pointercancel", finish);
    return () => {
      host.removeEventListener("pointerdown", onPointerDown);
      host.removeEventListener("pointermove", onPointerMove);
      host.removeEventListener("pointerup", finish);
      host.removeEventListener("pointercancel", finish);
    };
  }, []);

  // Trackpad horizontal swipe is wheel deltaX. Feed it into the same
  // overflow-y scroller vertical wheel uses (the 90deg rotation makes
  // that look like left/right), so snap/momentum match up/down. Without
  // a scroller, fall back to one tab-change per flick.
  useEffect(() => {
    if (isMobileLayout) return;
    const el = panelRef.current;
    if (!el) return;
    const TAB_THRESHOLD = 40;
    const RESTING_DELTA = 4;
    const SNAP_RESUME_MS = isBlink ? 320 : 120;
    const WHEEL_GAIN = isBlink ? 1.8 : 1;
    let accX = 0;
    let tabLocked = false;
    let snapTimer: number | undefined;
    let gestureStartScroll = 0;

    function resumeSnap(scroller: HTMLDivElement) {
      snapTimer = undefined;
      const i = pagedIndex(
        gestureStartScroll,
        scroller.scrollTop,
        scrollerMaxIndex(scroller)
      );
      animateScrollTop(scroller, i * CARD_STEP);
      window.setTimeout(() => {
        scroller.style.scrollSnapType = "";
      }, SNAP_ANIM_MS + 40);
    }

    function onWheelHorizontal(e: WheelEvent) {
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      e.preventDefault();
      const scroller = scrollRef.current;
      if (scroller) {
        const atEnd =
          scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 1;
        const atStart = scroller.scrollTop <= 1;
        if (e.deltaX > 0 && atEnd) {
          pageRef.current(1);
          return;
        }
        if (e.deltaX < 0 && atStart) {
          pageRef.current(-1);
          return;
        }
        if (!snapTimer) gestureStartScroll = scroller.scrollTop;
        scroller.style.scrollSnapType = "none";
        const visualScale = Math.max(scaleRef.current, 0.01);
        scroller.scrollTop += (e.deltaX / visualScale) * WHEEL_GAIN;
        if (snapTimer) window.clearTimeout(snapTimer);
        snapTimer = window.setTimeout(() => resumeSnap(scroller), SNAP_RESUME_MS);
        return;
      }

      if (tabLocked) {
        if (Math.abs(e.deltaX) <= RESTING_DELTA) {
          tabLocked = false;
          accX = 0;
        }
        return;
      }
      accX += e.deltaX;
      if (Math.abs(accX) < TAB_THRESHOLD) return;
      const direction: 1 | -1 = accX > 0 ? 1 : -1;
      accX = 0;
      if (!pageRef.current(direction)) return;
      tabLocked = true;
    }
    el.addEventListener("wheel", onWheelHorizontal, { passive: false });
    return () => {
      el.removeEventListener("wheel", onWheelHorizontal);
      if (snapTimer) window.clearTimeout(snapTimer);
    };
  }, [isMobileLayout]);

  function onScroll() {
    if (isProgrammaticScroll.current) return;
    const el = scrollRef.current;
    if (!el) return;
    setSlideIndex(Math.round(el.scrollTop / CARD_STEP));
  }

  return (
    <section className={styles["section"]}>
      <nav className={styles["tabs"]}>
        {slideshowTabs.map((tab) => (
          <button
            key={tab.key}
            className={`${styles["tab"]} ${tab.key === activeKey ? styles["tab-active"] : ""
              }`}
            onClick={() => selectTab(tab.key)}
            aria-selected={tab.key === activeKey}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <div ref={panelRef}>
        <div key={activeKey} className={`${styles["panel"]} ${styles["panel-transition"]}`}>
          {isRouterBridgeTab(activeTab) ? (
            <div className={styles["scale-wrapper"]} style={scaleWrapperStyle}>
              <div
                className={styles["slide-card-content"]}
                style={
                  isMobileLayout
                    ? { transform: "none" }
                    : { transform: `scale(${scale})`, transformOrigin: "top left" }
                }
              >
                <div className={styles["dual-layout"]}>
                  {activeTab.columns.map((col) => (
                    <div key={col.alt} className={styles["dual-column"]}>
                      <div className={styles["dual-title"]}>
                        <img src={col.titleIcon} alt="" className={styles["dual-title-icon"]} />
                        <h3 className={styles["dual-title-label"]}>{col.label}</h3>
                      </div>
                      {col.body && <p className={styles["dual-body"]}>{renderRichText(col.body)}</p>}
                      {col.imageCaption && (
                        <div className={styles["dual-image-caption"]}>{col.imageCaption}</div>
                      )}
                      <img
                        src={col.image}
                        alt={col.alt}
                        className={styles["dual-image"]}
                        loading="lazy"
                      />
                      {col.bodyAfterImage && (
                        <p className={styles["dual-body"]}>{col.bodyAfterImage}</p>
                      )}
                      {col.bullets && (
                        <ul className={styles["dual-bullets"]}>
                          {col.bullets.map((b) => (
                            <li key={b}>{renderRichText(b)}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : isMobileLayout ? (
            // Mobile: no rotated-scroll-container trick (fragile on touch) -
            // show one slide at a time, same as desktop, navigated via dots.
            <>
              <div
                className={`${styles["slide-card-content"]} ${activeTab.slides[slideIndex].layout === "top-bottom" ? styles["layout-top-bottom"] : ""
                  }`}
              >
                {renderSlideBody(activeTab.slides[slideIndex], activeTab)}
              </div>

              {activeTab.slides.length > 1 && (
                <div className={styles["dots"]}>
                  {activeTab.slides.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => goToSlide(i)}
                      className={`${styles["dot"]} ${i === slideIndex ? styles["dot-active"] : ""
                        }`}
                      aria-label={`Go to slide ${i + 1}`}
                      aria-current={i === slideIndex}
                    />
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              <div className={styles["scale-wrapper"]} style={scaleWrapperStyle}>
                <div className={styles["scroll-viewport"]} style={scaledContentStyle}>
                  <div className={styles["scroll-row"]} ref={scrollRef} onScroll={onScroll}>
                    {activeTab.slides.map((slide, i) => (
                      <div key={i} className={styles["slide-card"]}>
                        <div
                          className={`${styles["slide-card-content"]} ${slide.layout === "top-bottom" ? styles["layout-top-bottom"] : ""
                            }`}
                        >
                          {renderSlideBody(slide, activeTab)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {activeTab.slides.length > 1 && (
                <div className={styles["dots"]}>
                  {activeTab.slides.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => goToSlide(i)}
                      className={`${styles["dot"]} ${i === slideIndex ? styles["dot-active"] : ""
                        }`}
                      aria-label={`Go to slide ${i + 1}`}
                      aria-current={i === slideIndex}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default FeatureSlideshow;
